// src/main.js
import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import axios from 'axios'
import store from './store'
import '/index.css'

// Set base API URL from env
axios.defaults.baseURL = process.env.VUE_APP_APIURL

// --- NEW: register service worker + handle notification actions ---
if ('serviceWorker' in navigator) {
  // Register the SW you added at /public/sw.js (served at /sw.js)
  navigator.serviceWorker.register('/sw.js').catch(() => {})

  // Receive click actions from OS notification (posted by sw.js)
  navigator.serviceWorker.addEventListener('message', async (event) => {
    const msg = event?.data
    if (!msg || msg.type !== 'UC_NOTIFICATION_ACTION') return

    // Bring UI forward either way
    try {
      store.dispatch('phone/showPhone')
      store.dispatch('phone/setTab', 'dialer')
    } catch (_) {}

    if (msg.action === 'answer') {
      try {
        await store.dispatch('phone/answer')
      } catch (_) {
        // If mic/gesture rules block auto-answer, user can answer in-app.
      }
      return
    }

    if (msg.action === 'decline') {
      try {
        await store.dispatch('phone/hangup')
      } catch (_) {}
      return
    }
  })
}
// --- /NEW ---


// Axios request interceptor
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
}, error => {
  return Promise.reject(error)
})

// Axios response interceptor
axios.interceptors.response.use(response => {
  return response
}, error => {
  const token = localStorage.getItem('token')
  if (token && error.response && error.response.status === 401) {
    localStorage.removeItem('token')
    router.push({ name: 'login' })
    window.location.reload()
  }
  return Promise.reject(error)
})

// -------------------------------
// Presence connect helper (FIX)
// -------------------------------
let presenceConnectTimer = null
let presenceConnectTries = 0

function ensurePresenceConnected() {
  if (window.opener) return

  // stop any previous loop
  if (presenceConnectTimer) {
    clearTimeout(presenceConnectTimer)
    presenceConnectTimer = null
  }

  const tryConnect = () => {
    const token = localStorage.getItem('token')

    // If token exists, connect presence now
    if (token) {
      store.dispatch('presence/connect').catch(() => {})
      presenceConnectTries = 0
      presenceConnectTimer = null
      return
    }

    // No token yet — retry a bit (covers first login race)
    presenceConnectTries += 1

    // ~5 seconds total (50 * 100ms)
    if (presenceConnectTries > 50) {
      presenceConnectTries = 0
      presenceConnectTimer = null
      return
    }

    presenceConnectTimer = setTimeout(tryConnect, 100)
  }

  tryConnect()
}

// Mount app immediately
createApp(App).use(store).use(router).mount('#app')

// Set initialized after initial navigation completes
router.isReady().then(() => {
  store.commit('setInitialized', true)

  // ✅ Connect presence reliably even if token is written slightly after initial route ready
  ensurePresenceConnected()
})

// Also attempt presence connect after each route change (covers post-login redirects)
router.afterEach(() => {
  ensurePresenceConnected()
})

// reconnect presence when token changes (login/logout in another tab too)
window.addEventListener('storage', (e) => {
  if (e.key !== 'token') return
  const token = localStorage.getItem('token')
  if (token) {
    store.dispatch('presence/connect')
  } else {
    store.dispatch('presence/disconnect')
  }
})
